import { useConnectModal } from '@rainbow-me/rainbowkit'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Button from 'components/Button'
import Connector from 'containers/Connector'
import { setTradePanelDrawerOpen } from 'state/app/reducer'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { 
	selectMakeOrderFinished, 
	selectOrderDirection, 
	selectOrderErrorMessage, 
	selectOrderPrice, 
	selectOrderSize, 
	selectOrderTotal, 
	selectOrderType 
} from 'state/exchange/selectors'
import { placeOrder } from 'state/exchange/actions'
import { OrderDirection } from '@bitly/sdk/types'

const ManagePosition: React.FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { isWalletConnected } = Connector.useContainer()
	const { openConnectModal } = useConnectModal()
	const [submitted, setSubmitted] = useState(false)

	const orderDirection = useAppSelector(selectOrderDirection)
	const orderType = useAppSelector(selectOrderType)
	const orderPrice = Number(useAppSelector(selectOrderPrice))
	const orderSize = Number(useAppSelector(selectOrderSize))
	const orderTotal = Number(useAppSelector(selectOrderTotal))
	const isFinished = useAppSelector(selectMakeOrderFinished)
	const errorMessage = useAppSelector(selectOrderErrorMessage)

	const submitButtonDisabled = useMemo(() => {
		let disabled = false
		const volume = orderDirection === OrderDirection.buy ? orderTotal : orderSize
		if (orderType == 'market') {
			if (volume == 0) {
				disabled = true
			}
		} else {
			if (volume == 0 || orderPrice == 0) {
				disabled = true
			}
		}
		return disabled
	}, [orderType, orderSize, orderPrice, orderTotal])

	const onSubmit = useCallback(() => {
		dispatch(placeOrder())
		setSubmitted(true)
	}, [dispatch])

	useEffect(() => {
		if (isFinished && submitted) {
			dispatch(setTradePanelDrawerOpen(false))
		}
	}, [isFinished, submitted])

	const otherReason = useMemo(() => {
		if (!isWalletConnected) {
			return { key: 'futures.market.trade.button.connect-wallet', action: openConnectModal }
		} 
		return undefined
	}, [isWalletConnected, dispatch, openConnectModal])

	const placeOrderTextKey = useMemo(() => {
		if (orderType === 'limit') {
			return 'futures.market.trade.button.place-limit-order'
		} else {
			return 'futures.market.trade.button.place-market-order'
		}
	}, [orderType])

	return (
		<ManagePositionContainer>
			<PlaceOrderButton
				data-testid="trade-panel-submit-button"
				noOutline
				fullWidth
				loading={!isFinished}
				variant={otherReason ? 'yellow' : orderDirection}
				disabled={!otherReason && submitButtonDisabled || errorMessage !== undefined}
				onClick={otherReason?.action ?? onSubmit}
			>
				{t(otherReason?.key ?? placeOrderTextKey)}
			</PlaceOrderButton>
		</ManagePositionContainer>
	)
}

const ManagePositionContainer = styled.div`
	display: flex;
	grid-gap: 15px;
	margin-bottom: 16px;
`

const PlaceOrderButton = styled(Button)`
	font-size: 16px;
	height: 55px;
	text-align: center;
	white-space: normal;
`

export default ManagePosition
